import React, { useMemo, useState } from "react";
import { ButtonControl, FieldControl, IconControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { asArray, readValue } from "../wizard/resourceConfigWizardUtils";
import {
  CALENDAR_FREQUENCY_OPTIONS,
  CONVOKE_SELECTDATA_RUNTIME_CAPACITY_GRANT_OPERATION,
  CONVOKE_SELECTDATA_RUNTIME_CAPACITY_POOL_PROFILE_OPERATION,
  CORE_SELECTDATA_TIMEZONES_OPERATION,
  DESTINATION_SCHEDULE_FORM_GROUP_ID,
  DESTINATION_SCHEDULE_FREQUENCY_DAILY,
  DESTINATION_SCHEDULE_FREQUENCY_MONTHLY,
  DESTINATION_SCHEDULE_FREQUENCY_WEEKLY,
  DESTINATION_SCHEDULE_MODE_CALENDAR,
  DESTINATION_SCHEDULE_MODE_INTERVAL,
  DESTINATION_SCHEDULE_RUN_TIME_MODE_MANUAL,
  DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET,
  EXTERNAL_OPERATION_OPTIONS,
  fieldMetadata,
  fieldValue,
  LOCAL_RUN_TIME_OPTIONS,
  MONTH_DAY_OPTIONS,
  MONTHLY_ORDINAL_OPTIONS,
  normalizeNumber,
  normalizeNumberArray,
  normalizeStringArray,
  RUN_TIME_ENTRY_MODE_OPTIONS,
  SCHEDULE_MODE_OPTIONS,
  WAREHOUSE_STEP_OPTIONS,
  WEEKDAY_OPTIONS
} from "./destinationScheduleControlUtils";
import {
  DESTINATION_KIND_EXTERNAL_SYSTEM,
  DESTINATION_KIND_FILE_STORAGE,
  DESTINATION_KIND_WAREHOUSE,
  getDestinationKind
} from "../wizard/steps/destinationSetupStepUtils";
import WarehouseStepSelectorControl, {
  DEFAULT_WAREHOUSE_SYNC_STEP,
  getWarehouseModeForStep
} from "../WarehouseStepSelectorControl";

const weekdayLabel = (value) =>
  WEEKDAY_OPTIONS.find((option) => Number(option.Value) === Number(value))?.Text || value;

const ordinalLabel = (value) =>
  MONTHLY_ORDINAL_OPTIONS.find((option) => Number(option.Value) === Number(value))?.Text || value;

const fieldGroupId = (suffix = "") => `${DESTINATION_SCHEDULE_FORM_GROUP_ID}${suffix ? `-${suffix}` : ""}`;

const presetRunTimeValues = new Set(LOCAL_RUN_TIME_OPTIONS.map((option) => option.Value));

const ScheduleField = ({ schedule, field, value, onChange, disabled, selectData, className = "" }) => (
  <div className={`destination-schedule-control__field ${className}`.trim()}>
    <FieldControl
      className="destination-schedule-control__field-control"
      fieldMetadata={field}
      selectData={selectData}
      value={value}
      disabled={disabled}
      groupId={fieldGroupId(readValue(field, "FieldName", "fieldName"))}
      onChange={onChange}
      updatedItem={schedule}
      disableError
    />
  </div>
);

const ENABLE_SCHEDULE_DESCRIPTION = "Turns on scheduled execution for Apply Schedule and calendar dispatcher processing. Leave off to save the template without recurring runs; Run Now can still execute it manually.";
const WAREHOUSE_RESET_DATE_DESCRIPTION = "Move selected entity cursors back to this updated-date value before running.";
const WAREHOUSE_RESET_IDENTIFIER_DESCRIPTION = "Move selected entity cursors back to this source identifier. Date takes priority when both are set.";
const WAREHOUSE_RECONCILE_DESCRIPTION = "Re-check existing destination records during the load. Slower, but useful after mapping or data drift fixes.";
const WAREHOUSE_PARALLEL_DESCRIPTION = "Process multiple objects at once, limited by deployment max concurrency.";
const WAREHOUSE_FIRST_RUN_RESET_DESCRIPTION = "Clear destination processing state before starting. Only useful for first-run recovery.";

const optionLabel = (options, value, fallback = "") => {
  const match = options.find((option) => String(option.Value) === String(value));
  return match?.Text || match?.DisplayName || fallback || String(value ?? "");
};

const boolLabel = (value) => value === true ? "Yes" : "No";

const parseRuntimeCapacityGrantSelection = (value) => {
  const selectedValue = String(fieldValue(value) ?? "").trim();
  const [clientProductOfferId = "", capacityGrantKey = ""] = selectedValue.split("|");
  return {
    selectedValue,
    clientProductOfferId: clientProductOfferId.trim(),
    capacityGrantKey: capacityGrantKey.trim(),
    displayName: readValue(value, "Text", "text", "DisplayName", "displayName") || ""
  };
};

const ScheduleViewFacts = ({ facts }) => (
  <dl className="destination-schedule-control__view-facts">
    {facts.filter((fact) => fact.value !== undefined && fact.value !== null && fact.value !== "").map((fact) => (
      <div key={fact.label}>
        <dt>{fact.label}</dt>
        <dd>{String(fact.value)}</dd>
        {fact.description ? <p>{fact.description}</p> : null}
      </div>
    ))}
  </dl>
);

const EDITOR_TAB_SCHEDULE = "schedule";
const EDITOR_TAB_WAREHOUSE_SETUP = "warehouse-setup";
const EDITOR_TAB_DESTINATION_OPTIONS = "destination-options";

const EDITOR_TABS = {
  schedule: { key: EDITOR_TAB_SCHEDULE, label: "Schedule" },
  warehouseSetup: { key: EDITOR_TAB_WAREHOUSE_SETUP, label: "Warehouse Setup" },
  destinationOptions: { key: EDITOR_TAB_DESTINATION_OPTIONS, label: "Destination Options" }
};

const ScheduleEditorControl = ({
  schedule,
  defaults = {},
  mode = "setup",
  disabled = false,
  timezoneOptions = [],
  templateActions = [],
  isEditing = false,
  saving = false,
  showManagementEditorActions = true,
  runtimeCapacityPoolProfileOptions = [],
  onEdit,
  onSave,
  onCancelEdit,
  onChange
}) => {
  const [pendingMonthlyRule, setPendingMonthlyRule] = useState({ Ordinal: 1, DayOfWeek: 1 });
  const [activeEditorTab, setActiveEditorTab] = useState(EDITOR_TAB_SCHEDULE);
  const destinationKind = getDestinationKind(defaults);
  const isWarehouseDestination = destinationKind === DESTINATION_KIND_WAREHOUSE;
  const scheduleMode = Number(readValue(schedule, "ScheduleMode", "scheduleMode") || DESTINATION_SCHEDULE_MODE_INTERVAL);
  const calendarFrequency = Number(readValue(schedule, "CalendarFrequency", "calendarFrequency") || DESTINATION_SCHEDULE_FREQUENCY_DAILY);
  const localRunTimes = normalizeStringArray(readValue(schedule, "LocalRunTimes", "localRunTimes"));
  const hasManualRunTimes = localRunTimes.some((runTime) => !presetRunTimeValues.has(runTime));
  const runTimeEntryMode = Number(
    readValue(schedule, "LocalRunTimeEntryMode", "localRunTimeEntryMode")
    || (hasManualRunTimes ? DESTINATION_SCHEDULE_RUN_TIME_MODE_MANUAL : DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET)
  );
  const isManagementMode = mode === "management";
  const editable = !isManagementMode || isEditing;
  const fieldDisabled = disabled || !editable;
  const scopeDisplayName = readValue(schedule, "ScopeDisplayName", "scopeDisplayName") || "Scope not set";
  const scheduleName = readValue(schedule, "Name", "name") || "";
  const scheduleModeLabel = optionLabel(SCHEDULE_MODE_OPTIONS, scheduleMode, "Interval");
  const calendarFrequencyLabel = optionLabel(CALENDAR_FREQUENCY_OPTIONS, calendarFrequency, "Daily");
  const warehouseSyncStep = readValue(schedule, "WarehouseSyncStep", "warehouseSyncStep") || DEFAULT_WAREHOUSE_SYNC_STEP;
  const runtimeCapacityGrantDisplayName = readValue(schedule, "RuntimeCapacityGrantDisplayName", "runtimeCapacityGrantDisplayName") || "";
  const runtimeCapacityGrantValue = readValue(schedule, "RuntimeCapacityClientProductOfferId", "runtimeCapacityClientProductOfferId") || "";
  const runtimeCapacityPoolProfileId = readValue(schedule, "PoolProfileId", "poolProfileId") || "";
  const runtimeCapacityResolvedPoolProfileId = readValue(schedule, "RuntimeCapacityPoolProfileId", "runtimeCapacityPoolProfileId") || "";
  const selectedWarehouseMode = getWarehouseModeForStep(warehouseSyncStep);
  const editorTabs = isWarehouseDestination
    ? [EDITOR_TABS.schedule, EDITOR_TABS.warehouseSetup, EDITOR_TABS.destinationOptions]
    : [EDITOR_TABS.schedule, EDITOR_TABS.destinationOptions];
  const resolvedActiveEditorTab = editorTabs.some((tab) => tab.key === activeEditorTab)
    ? activeEditorTab
    : EDITOR_TAB_SCHEDULE;

  const updateSchedule = (fieldName, value) => {
    if (!schedule) return;
    onChange?.({
      ...schedule,
      [fieldName]: value
    });
  };

  const updateWarehouseStep = (nextStep, nextMode = null) => {
    if (!schedule) return;
    const modeDefinition = nextMode || getWarehouseModeForStep(nextStep);
    onChange?.({
      ...schedule,
      WarehouseSyncStep: nextStep || DEFAULT_WAREHOUSE_SYNC_STEP,
      WarehouseResetFromDate: modeDefinition?.supportsResetCursor ? readValue(schedule, "WarehouseResetFromDate", "warehouseResetFromDate") || "" : "",
      WarehouseResetFromValue: modeDefinition?.supportsResetCursor ? readValue(schedule, "WarehouseResetFromValue", "warehouseResetFromValue") || "" : "",
      WarehouseForceReconcile: modeDefinition?.supportsForceReconcile ? readValue(schedule, "WarehouseForceReconcile", "warehouseForceReconcile") === true : false,
      WarehouseMultiThread: modeDefinition?.supportsParallelProcessing ? readValue(schedule, "WarehouseMultiThread", "warehouseMultiThread") === true : false,
      WarehouseForceSync: modeDefinition?.supportsFirstRunReset ? readValue(schedule, "WarehouseForceSync", "warehouseForceSync") === true : false
    });
  };

  const updateScalar = (fieldName) => (nextValue) => updateSchedule(fieldName, fieldValue(nextValue));
  const updateNumber = (fieldName, fallback = 0) => (nextValue) => updateSchedule(fieldName, normalizeNumber(nextValue, fallback));
  const updateStringArray = (fieldName) => (nextValue) => updateSchedule(fieldName, normalizeStringArray(nextValue));
  const updateNumberArray = (fieldName) => (nextValue) => updateSchedule(fieldName, normalizeNumberArray(nextValue));
  const updateRuntimeCapacityGrant = (nextValue) => {
    if (!schedule) return;
    const selection = parseRuntimeCapacityGrantSelection(nextValue);
    onChange?.({
      ...schedule,
      RuntimeCapacityClientProductOfferId: selection.selectedValue,
      RuntimeCapacityGrantKey: selection.capacityGrantKey,
      RuntimeCapacityGrantDisplayName: selection.displayName,
      RuntimeCapacityProductOfferKey: "",
      RuntimeCapacityProductOfferSku: "",
      RuntimeCapacityPoolType: null,
      RuntimeCapacityIsUnlimited: false,
      RuntimeCapacityPoolProfileId: "",
      PoolProfileId: ""
    });
  };
  const updateRunTimeEntryMode = (nextValue) => {
    if (!schedule) return;
    const nextMode = normalizeNumber(nextValue, DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET);
    const currentRunTimes = normalizeStringArray(readValue(schedule, "LocalRunTimes", "localRunTimes"));
    const presetRunTimes = currentRunTimes.filter((runTime) => presetRunTimeValues.has(runTime));
    let nextRunTimes = currentRunTimes.length ? currentRunTimes : ["00:00"];
    if (nextMode === DESTINATION_SCHEDULE_RUN_TIME_MODE_PRESET) {
      nextRunTimes = presetRunTimes.length ? presetRunTimes : ["00:00"];
    }
    onChange?.({
      ...schedule,
      LocalRunTimeEntryMode: nextMode,
      LocalRunTimes: nextRunTimes
    });
  };

  const monthlyRules = useMemo(
    () => asArray(readValue(schedule, "MonthlyWeekdayRules", "monthlyWeekdayRules")),
    [schedule]
  );

  const addMonthlyRule = () => {
    updateSchedule("MonthlyWeekdayRules", [
      ...monthlyRules,
      {
        UniqueId: `monthly-rule-${Date.now()}`,
        Ordinal: Number(pendingMonthlyRule.Ordinal) || 1,
        DayOfWeek: Number(pendingMonthlyRule.DayOfWeek) || 1
      }
    ]);
  };

  const removeMonthlyRule = (index) => {
    updateSchedule("MonthlyWeekdayRules", monthlyRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const warehouseSetupFacts = [
    {
      label: "Warehouse Step",
      value: optionLabel(WAREHOUSE_STEP_OPTIONS, warehouseSyncStep, "Partial Warehouse Sync")
    }
  ];

  const destinationOptionFacts = [
    {
      label: "Runtime Capacity",
      value: runtimeCapacityGrantDisplayName || runtimeCapacityGrantValue || "Not selected"
    },
    {
      label: "Pool Profile",
      value: runtimeCapacityPoolProfileId || runtimeCapacityResolvedPoolProfileId || "Resolved at runtime"
    },
    ...(isManagementMode ? [
      { label: "Enabled", value: boolLabel(readValue(schedule, "EnableSchedule", "enableSchedule") === true) }
    ] : []),
    ...(isWarehouseDestination ? [
      ...(selectedWarehouseMode?.supportsResetCursor ? [
        { label: "Reset After Date", value: readValue(schedule, "WarehouseResetFromDate", "warehouseResetFromDate") || "Not set" },
        { label: "Reset After Identifier", value: readValue(schedule, "WarehouseResetFromValue", "warehouseResetFromValue") || "Not set" }
      ] : []),
      ...(selectedWarehouseMode?.supportsFirstRunReset ? [
        { label: "Reset First-Run State", value: boolLabel(readValue(schedule, "WarehouseForceSync", "warehouseForceSync") === true) }
      ] : []),
      ...(selectedWarehouseMode?.supportsForceReconcile ? [
        { label: "Reconcile Every Record", value: boolLabel(readValue(schedule, "WarehouseForceReconcile", "warehouseForceReconcile") === true) }
      ] : []),
      ...(selectedWarehouseMode?.supportsParallelProcessing ? [
        { label: "Run in Parallel", value: boolLabel(readValue(schedule, "WarehouseMultiThread", "warehouseMultiThread") === true) }
      ] : [])
    ] : []),
    ...(destinationKind === DESTINATION_KIND_FILE_STORAGE ? [
      {
        label: "Rebuild All Files",
        value: boolLabel(readValue(schedule, "FileStorageFullSync", "fileStorageFullSync") === true),
        description: "Loads all records in the selected scope and rebuilds the destination files. Leave off to deliver only records marked for file delivery."
      }
    ] : []),
    ...(destinationKind === DESTINATION_KIND_EXTERNAL_SYSTEM ? [
      { label: "Operation", value: optionLabel(EXTERNAL_OPERATION_OPTIONS, readValue(schedule, "ExternalSystemOperation", "externalSystemOperation") || 1, "Sync") },
      { label: "Take", value: readValue(schedule, "ExternalSystemTake", "externalSystemTake") || 250 }
    ] : [])
  ];

  if (!schedule) {
    return (
      <div className="destination-schedule-control__empty-editor">
        Select or add a schedule to configure it.
      </div>
    );
  }

  const enableScheduleField = isManagementMode ? (
    <ScheduleField
      schedule={schedule}
      field={fieldMetadata({
        fieldName: "EnableSchedule",
        displayName: "Enabled",
        description: ENABLE_SCHEDULE_DESCRIPTION,
        fieldType: SystemTypes.UIFieldType.CheckBox
      })}
      value={readValue(schedule, "EnableSchedule", "enableSchedule") === true}
      disabled={fieldDisabled}
      onChange={(nextValue) => updateSchedule("EnableSchedule", nextValue === true)}
    />
  ) : null;

  return (
    <div className="destination-schedule-control__editor">
      <div className="destination-schedule-control__editor-header">
        <div className="destination-schedule-control__section-title">
          <h4>{editable ? "Manage Schedule" : "Schedule"}</h4>
          <span className="destination-schedule-control__section-subtitle">{scopeDisplayName}</span>
        </div>
        <div className="destination-schedule-control__section-heading-actions">
          {showManagementEditorActions && isManagementMode && !editable ? (
            <button
              type="button"
              className="destination-schedule-control__icon-button"
              title="Edit schedule"
              aria-label={`Edit schedule ${scheduleName || "Destination Schedule"}`}
              onClick={onEdit}
              disabled={disabled}
            >
              <IconControl iconKey="edit" width="1.25rem" height="1.25rem" />
            </button>
          ) : null}
          {showManagementEditorActions && isManagementMode && editable ? (
            <button
              type="button"
              className="destination-schedule-control__icon-button"
              title="Cancel edit"
              aria-label={`Cancel edit ${scheduleName || "Destination Schedule"}`}
              onClick={onCancelEdit}
              disabled={saving}
            >
              <IconControl iconKey="close" width="1.25rem" height="1.25rem" />
            </button>
          ) : null}
        </div>
      </div>

      {templateActions.length ? (
        <div className="destination-schedule-control__template-action-bar destination-schedule-control__template-action-bar--above-tabs" role="toolbar" aria-label="Schedule template operations">
          {templateActions.map((action) => (
            <ButtonControl
              key={action.key}
              className="destination-schedule-control__template-action"
              type={action.type || "standard-small"}
              title={action.title}
              onClick={action.onClick}
              disabled={action.disabled}
              loading={action.loading || undefined}
            >
              {action.iconKey ? (
                <span className="destination-schedule-control__template-action-icon" aria-hidden="true">
                  <IconControl iconKey={action.iconKey} width="1rem" height="1rem" />
                </span>
              ) : null}
              <span>{action.label}</span>
            </ButtonControl>
          ))}
        </div>
      ) : null}

      <div className="form-wrapper form-wrapper-tabs-top destination-schedule-control__editor-tabs">
        <div className="form-tabs-container form-tabs-container-top destination-schedule-control__editor-tab-list" role="tablist" aria-label="Schedule form sections">
          {editorTabs.map((tab) => {
            const active = resolvedActiveEditorTab === tab.key;
            return (
              <button
                key={tab.key}
                id={`${fieldGroupId("editor")}-${tab.key}-tab`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`${fieldGroupId("editor")}-${tab.key}-panel`}
                className={`form-tab-rows destination-schedule-control__editor-tab ${active ? "form-tab-row-active destination-schedule-control__editor-tab--active" : ""}`}
                onClick={() => setActiveEditorTab(tab.key)}
              >
                <span className="form-tab">{tab.label}</span>
              </button>
            );
          })}
        </div>
        <div
          id={`${fieldGroupId("editor")}-${resolvedActiveEditorTab}-panel`}
          role="tabpanel"
          aria-labelledby={`${fieldGroupId("editor")}-${resolvedActiveEditorTab}-tab`}
          className="destination-schedule-control__editor-tab-panel"
        >

      {resolvedActiveEditorTab === EDITOR_TAB_SCHEDULE ? (
        <>
      <div className="destination-schedule-control__section destination-schedule-control__editor-form-section destination-schedule-control__schedule-fields-section">
        {editable ? (
          <div className="destination-schedule-control__field-grid">
            <ScheduleField
              schedule={schedule}
              field={fieldMetadata({ fieldName: "Name", displayName: "Schedule Name", isRequired: true })}
              value={scheduleName}
              disabled={fieldDisabled}
              onChange={updateScalar("Name")}
            />
            <ScheduleField
              schedule={schedule}
              field={fieldMetadata({ fieldName: "ScheduleMode", displayName: "Mode", fieldType: SystemTypes.UIFieldType.SelectList, options: SCHEDULE_MODE_OPTIONS, isRequired: true })}
              selectData={SCHEDULE_MODE_OPTIONS}
              value={scheduleMode}
              disabled={fieldDisabled}
              onChange={updateNumber("ScheduleMode", DESTINATION_SCHEDULE_MODE_INTERVAL)}
            />
          </div>
        ) : (
          <ScheduleViewFacts
            facts={[
              { label: "Schedule Name", value: scheduleName || "Destination Schedule" },
              { label: "Mode", value: scheduleModeLabel },
              { label: "Scope", value: scopeDisplayName }
            ]}
          />
        )}
      </div>

      {scheduleMode === DESTINATION_SCHEDULE_MODE_INTERVAL ? (
        <div className="destination-schedule-control__section destination-schedule-control__editor-form-section">
          {editable ? (
            <>
              <div className="destination-schedule-control__field-grid">
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({
                    fieldName: "RecurrenceIntervalMinutes",
                    displayName: "Interval Minutes",
                    description: "Runs first when the schedule is applied, unless a Start Date is set, then repeats every N minutes while enabled until the optional End Date.",
                    fieldType: SystemTypes.UIFieldType.TextBox,
                    format: SystemTypes.UIFormats?.INT
                  })}
                  value={readValue(schedule, "RecurrenceIntervalMinutes", "recurrenceIntervalMinutes") || 60}
                  disabled={fieldDisabled}
                  onChange={updateNumber("RecurrenceIntervalMinutes", 60)}
                />
              </div>
              {enableScheduleField ? (
                <div className="destination-schedule-control__field-grid destination-schedule-control__field-grid--destination-options">
                  {enableScheduleField}
                </div>
              ) : null}
            </>
          ) : (
            <ScheduleViewFacts
              facts={[
                {
                  label: "Interval Minutes",
                  value: readValue(schedule, "RecurrenceIntervalMinutes", "recurrenceIntervalMinutes") || 60,
                  description: "Runs first when the schedule is applied, unless a Start Date is set, then repeats every N minutes while enabled until the optional End Date."
                }
              ]}
            />
          )}
        </div>
      ) : null}

      {scheduleMode === DESTINATION_SCHEDULE_MODE_CALENDAR ? (
        <div className="destination-schedule-control__section destination-schedule-control__editor-form-section">
          {editable ? (
            <>
              <div className="destination-schedule-control__field-grid">
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({
                    fieldName: "TimeZoneId",
                    displayName: "Timezone",
                    fieldType: SystemTypes.UIFieldType.SearchSelectList,
                    dataOperation: CORE_SELECTDATA_TIMEZONES_OPERATION,
                    dataOperationApp: "Core",
                    isRequired: true
                  })}
                  selectData={timezoneOptions}
                  value={readValue(schedule, "TimeZoneId", "timeZoneId") || "Eastern Standard Time|America/New_York"}
                  disabled={fieldDisabled}
                  onChange={updateScalar("TimeZoneId")}
                />
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({ fieldName: "LocalRunTimeEntryMode", displayName: "Run Time Mode", fieldType: SystemTypes.UIFieldType.SelectList, options: RUN_TIME_ENTRY_MODE_OPTIONS })}
                  selectData={RUN_TIME_ENTRY_MODE_OPTIONS}
                  value={runTimeEntryMode}
                  disabled={fieldDisabled}
                  onChange={updateRunTimeEntryMode}
                />
                {runTimeEntryMode === DESTINATION_SCHEDULE_RUN_TIME_MODE_MANUAL ? (
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({ fieldName: "LocalRunTimes", displayName: "Run Times", description: "Comma-separated local HH:mm values." })}
                value={localRunTimes.join(", ")}
                disabled={fieldDisabled}
                onChange={updateStringArray("LocalRunTimes")}
              />
            ) : (
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({
                  fieldName: "LocalRunTimes",
                  displayName: "Run Times",
                  description: "Select one or more local run times.",
                  fieldType: SystemTypes.UIFieldType.MultiSelectList,
                  options: LOCAL_RUN_TIME_OPTIONS
                })}
                selectData={LOCAL_RUN_TIME_OPTIONS}
                value={localRunTimes}
                disabled={fieldDisabled}
                onChange={updateStringArray("LocalRunTimes")}
              />
            )}
            <ScheduleField
              schedule={schedule}
              field={fieldMetadata({ fieldName: "CalendarFrequency", displayName: "Frequency", fieldType: SystemTypes.UIFieldType.SelectList, options: CALENDAR_FREQUENCY_OPTIONS })}
              selectData={CALENDAR_FREQUENCY_OPTIONS}
              value={calendarFrequency}
              disabled={fieldDisabled}
              onChange={updateNumber("CalendarFrequency", DESTINATION_SCHEDULE_FREQUENCY_DAILY)}
            />
            {calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_DAILY ? (
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({ fieldName: "DailyIntervalDays", displayName: "Every N Days" })}
                value={readValue(schedule, "DailyIntervalDays", "dailyIntervalDays") || 1}
                disabled={fieldDisabled}
                onChange={updateNumber("DailyIntervalDays", 1)}
              />
            ) : null}
            {calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_WEEKLY ? (
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({ fieldName: "WeeklyDays", displayName: "Weekdays", fieldType: SystemTypes.UIFieldType.MultiSelectList, options: WEEKDAY_OPTIONS })}
                selectData={WEEKDAY_OPTIONS}
                value={normalizeNumberArray(readValue(schedule, "WeeklyDays", "weeklyDays"))}
                disabled={fieldDisabled}
                onChange={updateNumberArray("WeeklyDays")}
              />
            ) : null}
            {calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_MONTHLY ? (
              <>
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({
                    fieldName: "MonthlyDays",
                    displayName: "Monthly Days",
                    description: "Select one or more calendar days.",
                    fieldType: SystemTypes.UIFieldType.MultiSelectList,
                    options: MONTH_DAY_OPTIONS
                  })}
                  selectData={MONTH_DAY_OPTIONS}
                  value={normalizeNumberArray(readValue(schedule, "MonthlyDays", "monthlyDays"))}
                  disabled={fieldDisabled}
                  onChange={updateNumberArray("MonthlyDays")}
                />
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({ fieldName: "UseLastDayForShortMonths", displayName: "Use Last Day For Short Months", fieldType: SystemTypes.UIFieldType.CheckBox })}
                  value={readValue(schedule, "UseLastDayForShortMonths", "useLastDayForShortMonths") !== false}
                  disabled={fieldDisabled}
                  onChange={(nextValue) => updateSchedule("UseLastDayForShortMonths", nextValue === true)}
                />
              </>
            ) : null}
          </div>

          {calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_MONTHLY ? (
            <div className="destination-schedule-control__monthly-rules">
              <div className="destination-schedule-control__section-heading destination-schedule-control__section-heading--compact">
                <h4>Monthly Weekday Rules</h4>
              </div>
              <div className="destination-schedule-control__field-grid">
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({ fieldName: "PendingMonthlyOrdinal", displayName: "Ordinal", fieldType: SystemTypes.UIFieldType.SelectList, options: MONTHLY_ORDINAL_OPTIONS })}
                  selectData={MONTHLY_ORDINAL_OPTIONS}
                  value={pendingMonthlyRule.Ordinal}
                  disabled={fieldDisabled}
                  onChange={(nextValue) => setPendingMonthlyRule((previous) => ({ ...previous, Ordinal: normalizeNumber(nextValue, 1) }))}
                />
                <ScheduleField
                  schedule={schedule}
                  field={fieldMetadata({ fieldName: "PendingMonthlyWeekday", displayName: "Weekday", fieldType: SystemTypes.UIFieldType.SelectList, options: WEEKDAY_OPTIONS })}
                  selectData={WEEKDAY_OPTIONS}
                  value={pendingMonthlyRule.DayOfWeek}
                  disabled={fieldDisabled}
                  onChange={(nextValue) => setPendingMonthlyRule((previous) => ({ ...previous, DayOfWeek: normalizeNumber(nextValue, 1) }))}
                />
                <div className="destination-schedule-control__field destination-schedule-control__field--button">
                  <ButtonControl type="standard-small" onClick={addMonthlyRule} disabled={fieldDisabled}>
                    Add Rule
                  </ButtonControl>
                </div>
              </div>
              <div className="destination-schedule-control__rule-list">
                {monthlyRules.length ? monthlyRules.map((rule, index) => (
                  <div className="destination-schedule-control__rule" key={readValue(rule, "UniqueId", "uniqueId") || `${readValue(rule, "Ordinal", "ordinal")}-${readValue(rule, "DayOfWeek", "dayOfWeek")}-${index}`}>
                    <span>{ordinalLabel(readValue(rule, "Ordinal", "ordinal"))} {weekdayLabel(readValue(rule, "DayOfWeek", "dayOfWeek"))}</span>
                    <ButtonControl type="standard-small" onClick={() => removeMonthlyRule(index)} disabled={fieldDisabled}>
                      Remove
                    </ButtonControl>
                  </div>
                )) : (
                  <div className="destination-schedule-control__empty-note">No monthly weekday rules added.</div>
                )}
              </div>
            </div>
          ) : null}
          {enableScheduleField ? (
            <div className="destination-schedule-control__field-grid destination-schedule-control__field-grid--destination-options">
              {enableScheduleField}
            </div>
          ) : null}
            </>
          ) : (
            <ScheduleViewFacts
              facts={[
                { label: "Timezone", value: readValue(schedule, "TimeZoneId", "timeZoneId") || "Eastern Standard Time|America/New_York" },
                { label: "Run Time Mode", value: optionLabel(RUN_TIME_ENTRY_MODE_OPTIONS, runTimeEntryMode, "Preset") },
                { label: "Run Times", value: localRunTimes.join(", ") || "00:00" },
                { label: "Frequency", value: calendarFrequencyLabel },
                ...(calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_DAILY ? [
                  { label: "Every N Days", value: readValue(schedule, "DailyIntervalDays", "dailyIntervalDays") || 1 }
                ] : []),
                ...(calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_WEEKLY ? [
                  { label: "Weekdays", value: normalizeNumberArray(readValue(schedule, "WeeklyDays", "weeklyDays")).map(weekdayLabel).join(", ") || "Not set" }
                ] : []),
                ...(calendarFrequency === DESTINATION_SCHEDULE_FREQUENCY_MONTHLY ? [
                  { label: "Monthly Days", value: normalizeNumberArray(readValue(schedule, "MonthlyDays", "monthlyDays")).join(", ") || "Not set" },
                  { label: "Use Last Day For Short Months", value: boolLabel(readValue(schedule, "UseLastDayForShortMonths", "useLastDayForShortMonths") !== false) },
                  { label: "Monthly Weekday Rules", value: monthlyRules.map((rule) => `${ordinalLabel(readValue(rule, "Ordinal", "ordinal"))} ${weekdayLabel(readValue(rule, "DayOfWeek", "dayOfWeek"))}`).join(", ") || "None" }
                ] : [])
              ]}
            />
          )}
        </div>
      ) : null}

        </>
      ) : null}

      {resolvedActiveEditorTab === EDITOR_TAB_WAREHOUSE_SETUP && isWarehouseDestination ? (
        <div className="destination-schedule-control__section destination-schedule-control__editor-form-section destination-schedule-control__warehouse-setup-section">
          <div className="destination-schedule-control__section-heading">
            <h4>Warehouse Setup</h4>
          </div>
          {editable ? (
            <WarehouseStepSelectorControl
              className="destination-schedule-control__warehouse-step-selector"
              value={warehouseSyncStep}
              onChange={updateWarehouseStep}
              disabled={fieldDisabled}
              emptyContent="No warehouse steps are available for this schedule."
            />
          ) : (
            <ScheduleViewFacts facts={warehouseSetupFacts} />
          )}
        </div>
      ) : null}

      {resolvedActiveEditorTab === EDITOR_TAB_DESTINATION_OPTIONS ? (
        <div className="destination-schedule-control__section destination-schedule-control__editor-form-section">
        <div className="destination-schedule-control__section-heading">
          <div className="destination-schedule-control__section-title">
            <h4>Destination Options</h4>
            {isWarehouseDestination && selectedWarehouseMode?.description ? (
              <span className="destination-schedule-control__section-subtitle">{selectedWarehouseMode.description}</span>
            ) : null}
          </div>
        </div>
        {editable ? (
          <div className="destination-schedule-control__field-grid destination-schedule-control__field-grid--destination-options">
            <ScheduleField
              schedule={schedule}
              field={fieldMetadata({
                fieldName: "RuntimeCapacityClientProductOfferId",
                displayName: "Runtime Capacity Grant",
                description: "Purchased runtime capacity grant required for scheduled pool-backed execution.",
                fieldType: SystemTypes.UIFieldType.SearchSelectList,
                dataOperation: CONVOKE_SELECTDATA_RUNTIME_CAPACITY_GRANT_OPERATION,
                dataOperationApp: "Convoke",
                isRequired: true
              })}
              value={runtimeCapacityGrantValue}
              disabled={fieldDisabled}
              onChange={updateRuntimeCapacityGrant}
            />
            <ScheduleField
              schedule={schedule}
              field={fieldMetadata({
                fieldName: "PoolProfileId",
                displayName: "Pool Profile",
                description: "Optional pool profile filtered by the selected runtime capacity grant.",
                fieldType: SystemTypes.UIFieldType.SearchSelectList,
                dataOperation: CONVOKE_SELECTDATA_RUNTIME_CAPACITY_POOL_PROFILE_OPERATION,
                dataOperationApp: "Convoke",
                dataOperationFilterProperty: "RuntimeCapacityClientProductOfferId"
              })}
              value={runtimeCapacityPoolProfileId}
              selectData={runtimeCapacityPoolProfileOptions}
              disabled={fieldDisabled || !runtimeCapacityGrantValue}
              onChange={updateScalar("PoolProfileId")}
            />
            {isWarehouseDestination ? (
              <>
              {selectedWarehouseMode?.supportsResetCursor ? (
                <>
                  <ScheduleField schedule={schedule} field={fieldMetadata({ fieldName: "WarehouseResetFromDate", displayName: "Reset After Date", description: WAREHOUSE_RESET_DATE_DESCRIPTION, fieldType: SystemTypes.UIFieldType.DateTime })} value={readValue(schedule, "WarehouseResetFromDate", "warehouseResetFromDate") || ""} disabled={fieldDisabled} onChange={updateScalar("WarehouseResetFromDate")} />
                  <ScheduleField schedule={schedule} field={fieldMetadata({ fieldName: "WarehouseResetFromValue", displayName: "Reset After Identifier", description: WAREHOUSE_RESET_IDENTIFIER_DESCRIPTION })} value={readValue(schedule, "WarehouseResetFromValue", "warehouseResetFromValue") || ""} disabled={fieldDisabled} onChange={updateScalar("WarehouseResetFromValue")} />
                </>
              ) : null}
              {selectedWarehouseMode?.supportsFirstRunReset ? (
                <ScheduleField schedule={schedule} field={fieldMetadata({ fieldName: "WarehouseForceSync", displayName: "Reset First-Run State", description: WAREHOUSE_FIRST_RUN_RESET_DESCRIPTION, fieldType: SystemTypes.UIFieldType.CheckBox })} value={readValue(schedule, "WarehouseForceSync", "warehouseForceSync") === true} disabled={fieldDisabled} onChange={(nextValue) => updateSchedule("WarehouseForceSync", nextValue === true)} />
              ) : null}
              {selectedWarehouseMode?.supportsForceReconcile ? (
                <ScheduleField schedule={schedule} field={fieldMetadata({ fieldName: "WarehouseForceReconcile", displayName: "Reconcile Every Record", description: WAREHOUSE_RECONCILE_DESCRIPTION, fieldType: SystemTypes.UIFieldType.CheckBox })} value={readValue(schedule, "WarehouseForceReconcile", "warehouseForceReconcile") === true} disabled={fieldDisabled} onChange={(nextValue) => updateSchedule("WarehouseForceReconcile", nextValue === true)} />
              ) : null}
              {selectedWarehouseMode?.supportsParallelProcessing ? (
                <ScheduleField schedule={schedule} field={fieldMetadata({ fieldName: "WarehouseMultiThread", displayName: "Run in Parallel", description: WAREHOUSE_PARALLEL_DESCRIPTION, fieldType: SystemTypes.UIFieldType.CheckBox })} value={readValue(schedule, "WarehouseMultiThread", "warehouseMultiThread") === true} disabled={fieldDisabled} onChange={(nextValue) => updateSchedule("WarehouseMultiThread", nextValue === true)} />
              ) : null}
              </>
            ) : null}
            {destinationKind === DESTINATION_KIND_FILE_STORAGE ? (
              <>
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({
                  fieldName: "FileStorageFullSync",
                  displayName: "Rebuild All Files",
                  description: "Loads all records in the selected scope and rebuilds the destination files. Leave off to deliver only records marked for file delivery.",
                  fieldType: SystemTypes.UIFieldType.CheckBox
                })}
                value={readValue(schedule, "FileStorageFullSync", "fileStorageFullSync") === true}
                disabled={fieldDisabled}
                onChange={(nextValue) => updateSchedule("FileStorageFullSync", nextValue === true)}
              />
              </>
            ) : null}
            {destinationKind === DESTINATION_KIND_EXTERNAL_SYSTEM ? (
              <>
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({ fieldName: "ExternalSystemOperation", displayName: "Operation", fieldType: SystemTypes.UIFieldType.SelectList, options: EXTERNAL_OPERATION_OPTIONS })}
                selectData={EXTERNAL_OPERATION_OPTIONS}
                value={readValue(schedule, "ExternalSystemOperation", "externalSystemOperation") || 1}
                disabled={fieldDisabled}
                onChange={updateNumber("ExternalSystemOperation", 1)}
              />
              <ScheduleField
                schedule={schedule}
                field={fieldMetadata({ fieldName: "ExternalSystemTake", displayName: "Take" })}
                value={readValue(schedule, "ExternalSystemTake", "externalSystemTake") || 250}
                disabled={fieldDisabled}
                onChange={updateNumber("ExternalSystemTake", 250)}
              />
              </>
            ) : null}
          </div>
        ) : (
          <ScheduleViewFacts facts={destinationOptionFacts} />
        )}
      </div>
      ) : null}
        </div>
      </div>
      {showManagementEditorActions && isManagementMode && editable ? (
        <div className="destination-schedule-control__edit-actions">
          <ButtonControl type="cancel-small" onClick={onCancelEdit} disabled={saving}>
            Cancel
          </ButtonControl>
          <ButtonControl type="ok-small" onClick={onSave} disabled={disabled || saving} loading={saving || undefined}>
            Save
          </ButtonControl>
        </div>
      ) : null}
    </div>
  );
};

export default ScheduleEditorControl;
